import { FramedList, FramedListRow } from '@kerfjs/ui/framed-list';
import { List } from '@kerfjs/ui/list';
import { ListHeader } from '@kerfjs/ui/list-header';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { Row } from '@kerfjs/ui/row';
import { Text } from '@kerfjs/ui/text';
import type { SafeHtml } from 'kerfjs';
import { Info, ScanSearch, TriangleAlert } from 'lucide';

import type { QuotationReviewT, ReviewFindingT } from '../../../../../../shared/contract/analysis.js';
import { reviewFindingGroups, reviewFindingMeta } from '../../../../../../shared/quotation-review.js';

function FindingRow({ finding }: { finding: ReviewFindingT }): SafeHtml {
  const high = finding.severity === 'high';
  return (
    <FramedListRow rootAttributes={{ 'data-review-finding': finding.kind, 'data-severity': finding.severity }}>
      <Row vAlign="baseline" gap="xs">
        {/* A rigid box keeps the icon full size while the message wraps at phone width. */}
        <Row flex="none">
          <Text variant="span" tone={high ? 'danger' : 'quiet'}>
            <LucideIcon
              icon={high ? TriangleAlert : finding.severity === 'medium' ? ScanSearch : Info}
              name=""
              inline
            />
          </Text>
        </Row>
        <List flex>
          <Text flush>{finding.message}</Text>
          <Text flush tone="quiet" size="compact">
            {reviewFindingMeta(finding)}
          </Text>
        </List>
      </Row>
    </FramedListRow>
  );
}

/**
 * The independent quotation review at the top of Summary (FR-3.26a, HS2-XG16AM):
 * the AI's plain-language recommendation, then every finding grouped by severity,
 * each naming the quotation, document, and row it was read from. Hidden on a run
 * that had no quotations, and on versions from before the review existed.
 */
export function OverviewReviewSection({ review }: { review: QuotationReviewT | undefined }): SafeHtml {
  if (review === undefined || review.quotationCount === 0) return <></>;
  const groups = reviewFindingGroups(review);
  return (
    <List className="overview-review-section" gap="xs" rootAttributes={{ 'data-quotation-review': '' }}>
      <ListHeader label="Quotation Review" />
      {review.narrative !== null ? (
        <Text controlMargins="rl">{review.narrative}</Text>
      ) : review.aiStatus === 'failed' ? (
        <Text tone="quiet" size="compact" controlMargins="rl">
          The AI review could not run for this version; the checks below are deterministic.
        </Text>
      ) : null}
      {groups.length === 0 ? (
        <Text tone="quiet" controlMargins="rl">
          No issues found in the quotations.
        </Text>
      ) : (
        groups.map((group) => (
          <List>
            <ListHeader label={group.label} size="xs" count={group.findings.length} countLabel="findings" />
            <FramedList label={group.label}>
              {group.findings.map((finding) => (
                <FindingRow finding={finding} />
              ))}
            </FramedList>
          </List>
        ))
      )}
    </List>
  );
}
